import * as Sentry from '@sentry/react';
import { auth, setupRecaptcha as firebaseSetupRecaptcha } from '../config/firebase';
import { toast } from 'react-hot-toast';

const MAX_ATTEMPTS = 3;
const RATE_LIMIT_WINDOW = 10 * 60 * 1000;
const OTP_EXPIRY = 5 * 60 * 1000;

export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return { valid: false, error: 'Phone number is required' };
  const cleaned = phoneNumber.replace(/[\s-]/g, '');
  const phoneRegex = /^\+[1-9]\d{6,14}$/;
  if (!phoneRegex.test(cleaned)) {
    return { valid: false, error: 'Invalid phone number' };
  }
  return { valid: true, formatted: cleaned };
};

export const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  const cleaned = phoneNumber.replace(/[\s-]/g, '');
  if (cleaned.startsWith('+1') && cleaned.length === 12) {
    return `+1 (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
  }
  return phoneNumber;
};

export const checkRateLimit = () => {
  try {
    const data = JSON.parse(localStorage.getItem('smsRateLimit') || '{}');
    const now = Date.now();
    if (data.windowStart && now - data.windowStart > RATE_LIMIT_WINDOW) {
      localStorage.removeItem('smsRateLimit');
      return { allowed: true, remainingAttempts: MAX_ATTEMPTS };
    }
    const remaining = MAX_ATTEMPTS - (data.attempts || 0);
    if (remaining <= 0) {
      return { allowed: false, remainingAttempts: 0, waitTime: RATE_LIMIT_WINDOW - (now - data.windowStart) };
    }
    return { allowed: true, remainingAttempts: remaining };
  } catch {
    return { allowed: true, remainingAttempts: MAX_ATTEMPTS };
  }
};

const recordSmsAttempt = () => {
  try {
    const data = JSON.parse(localStorage.getItem('smsRateLimit') || '{}');
    const now = Date.now();
    if (!data.windowStart || now - data.windowStart > RATE_LIMIT_WINDOW) {
      localStorage.setItem('smsRateLimit', JSON.stringify({ windowStart: now, attempts: 1 }));
    } else {
      data.attempts = (data.attempts || 0) + 1;
      localStorage.setItem('smsRateLimit', JSON.stringify(data));
    }
  } catch (e) {
    console.error('Error recording SMS attempt:', e);
  }
};

const storeOtpSentTime = () => {
  localStorage.setItem('otpSentTime', Date.now().toString());
};

export const isOtpExpired = () => {
  try {
    const sentTime = parseInt(localStorage.getItem('otpSentTime') || '0', 10);
    return Date.now() - sentTime > OTP_EXPIRY;
  } catch {
    return true;
  }
};

export const getOtpRemainingTime = () => {
  try {
    const sentTime = parseInt(localStorage.getItem('otpSentTime') || '0', 10);
    const remaining = Math.max(0, OTP_EXPIRY - (Date.now() - sentTime));
    return Math.ceil(remaining / 1000);
  } catch {
    return 0;
  }
};

export const setupRecaptcha = async (containerId, options) => {
  return firebaseSetupRecaptcha(containerId, options);
};

export const sendPhoneOTP = async (phoneNumber, recaptchaVerifier = null) => {
  const rateLimitStatus = checkRateLimit();
  if (!rateLimitStatus.allowed) {
    return { success: false, rateLimited: true, waitTime: rateLimitStatus.waitTime };
  }

  const validation = validatePhoneNumber(phoneNumber);
  if (!validation.valid) {
    return { success: false, message: validation.error };
  }

  const verifier = recaptchaVerifier || window.recaptchaVerifier;
  if (!verifier) {
    return { success: false, message: 'Security check not ready. Please wait a moment and try again.' };
  }

  try {
    // The member is already signed in (Google / Apple / email / invite).
    // signInWithPhoneNumber would send SMS, then replace that session with
    // a phone-only Auth user — membership, seats, and invoices go missing.
    // Link the number onto the existing user instead. No currentUser is
    // the rare guest-only path.
    const currentUser = auth.currentUser;
    const confirmationResult = currentUser
      ? await currentUser.linkWithPhoneNumber(validation.formatted, verifier)
      : await auth.signInWithPhoneNumber(validation.formatted, verifier);
    recordSmsAttempt();
    storeOtpSentTime();
    toast.success('Code sent!');
    return { success: true, confirmationResult, remainingAttempts: checkRateLimit().remainingAttempts };
  } catch (error) {
    console.error('Send OTP error:', error);
    Sentry.captureException(error, {
      tags: { area: 'phone-otp', code: error.code || 'unknown' },
    });
    if (
      (error.code === 'auth/invalid-app-credential' || error.code === 'auth/captcha-check-failed') &&
      window.recaptchaVerifier
    ) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {
        // Ignore
      }
      window.recaptchaVerifier = null;
    }
    if (error.code === 'auth/provider-already-linked') {
      return { success: true, alreadyLinked: true, confirmationResult: null };
    }
    const messages = {
      'auth/invalid-phone-number': 'Invalid phone number. Please check and try again.',
      'auth/too-many-requests': 'Too many requests. Please wait a few minutes.',
      'auth/quota-exceeded': 'SMS quota exceeded. Please try again later.',
      'auth/operation-not-allowed': 'Phone verification is temporarily unavailable. Please try again later.',
      'auth/invalid-app-credential':
        'Security check expired. Please complete it again and click Send Code.',
      'auth/captcha-check-failed':
        'Security verification failed. Please try again.',
      'auth/credential-already-in-use':
        'This phone number is already used on another account. Try a different number.',
      'auth/account-exists-with-different-credential':
        'This phone number is already used on another account. Try a different number.',
      'auth/network-request-failed':
        'Network error. Check your connection and try again.',
    };
    return { success: false, message: messages[error.code] || 'Failed to send code', errorCode: error.code };
  }
};

export const verifyPhoneOTP = async (confirmationResult, code) => {
  if (!confirmationResult) {
    return { success: false, message: 'No verification in progress' };
  }

  if (isOtpExpired()) {
    return { success: false, expired: true, message: 'Code expired' };
  }

  try {
    const result = await confirmationResult.confirm(code);
    toast.success('Phone verified!');
    return { success: true, user: result.user };
  } catch (error) {
    if (error.code === 'auth/code-expired') {
      return { success: false, expired: true };
    }
    return { success: false, message: 'Invalid code' };
  }
};

export const clearPhoneVerificationData = () => {
  try {
    localStorage.removeItem('otpSentTime');
  } catch (e) {
    console.error('Error clearing data:', e);
  }
};

const phoneVerificationService = {
  validatePhoneNumber,
  formatPhoneForDisplay,
  checkRateLimit,
  isOtpExpired,
  getOtpRemainingTime,
  setupRecaptcha,
  sendPhoneOTP,
  verifyPhoneOTP,
  clearPhoneVerificationData,
};

export default phoneVerificationService;
